import React, { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  useTheme,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { useLocation } from 'react-router-dom';
import { HASH } from '../utils/helpers';
import { subs_completed } from '../services/api';

// Genera una contraseña: 2 letras minúsculas + 4 números (como Usuarios.js)
const generatePassword = () => {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  let password = '';
  // 2 letras aleatorias
  for (let i = 0; i < 2; i++) {
    password += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  // 4 números aleatorios
  for (let i = 0; i < 4; i++) {
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  }
  return password;
};

const NewSubscription = () => {
  const theme = useTheme();
  const [useremail, setUseremail] = useState('');
  const [username, setUsername] = useState('');
  const [prdname, setPrdname] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const linkcode = params.get('linkcode') || '';

  const isValidEmail = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(useremail);
  }, [useremail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!useremail || !username || !prdname) {
      setError('Por favor, completa todos los campos.');
      return;
    }
    if (!isValidEmail) {
      setError('Por favor, introduce un correo electrónico válido.');
      return;
    }
    if (!linkcode) {
      setError('El enlace no es válido.');
      return;
    }

    setLoading(true);
    try {
      const passvisible = generatePassword();
      const passhash = HASH(passvisible);
      const payload = {
        useremail,
        username,
        prdname,
        passhash,
        passvisible,
        linkcode
      };
      // Llamada a la API centralizada
      const response = await subs_completed(
        useremail,
        username,
        prdname,
        passhash,
        passvisible,
        linkcode
      );
      if (response && response.result === 0) {
        setMessage('¡Suscripción completada! Revisa tu correo para más instrucciones.');
        setUseremail('');
        setUsername('');
        setPrdname('');
        setSuccess(true);
      } else {
        setError(response?.error || 'Error al completar la suscripción.');
      }
    } catch (err) {
      setError('Error al completar la suscripción.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <PersonAddAlt1Icon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
            <Typography component="h1" variant="h5" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', mb: 2 }}>
              Nueva Suscripción
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              {!success && (
                <>
                  <TextField
                margin="normal"
                required
                fullWidth
                id="useremail"
                label="Correo electrónico"
                name="useremail"
                autoComplete="email"
                autoFocus
                value={useremail}
                onChange={e => setUseremail(e.target.value)}
                sx={{
                  input: { color: theme.palette.text.primary },
                  label: { color: theme.palette.text.secondary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
                InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Nombre del usuario"
                name="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                sx={{
                  input: { color: theme.palette.text.primary },
                  label: { color: theme.palette.text.secondary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
                InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="prdname"
                label="Nombre del asistente"
                name="prdname"
                value={prdname}
                onChange={e => setPrdname(e.target.value)}
                sx={{
                  input: { color: theme.palette.text.primary },
                  label: { color: theme.palette.text.secondary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
                InputLabelProps={{ style: { color: theme.palette.text.secondary } }}
              />
                </>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}
              {message && (
                <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>
              )}
              {!success ? (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 600, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', '&:hover': { backgroundColor: theme.palette.primary.dark } }}
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Crear Suscripción'}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 600, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', '&:hover': { backgroundColor: theme.palette.primary.dark } }}
                  onClick={() => window.location.href = 'https://admin.ai4logic.com/#/login'}
                >
                  Volver a inicio
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default NewSubscription;
